import { useState } from "react";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import { getProfileSubtitle } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useBlacklist from "../../datahooks/useBlacklist";
import useBlacklistMutations from "../../datahooks/useBlacklistMutations";
import AddToBlacklistButton from "./AddToBlacklistButton/AddToBlacklistButton";
import BlacklistItem from "./BlacklistItem";

import type { BlacklistType } from "../../types";

import EmptyStateImage from "assets/images/empty-placeholders/blacklist.png";
import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

export default function Blacklist({ type }: { type: BlacklistType }) {
  const isDesktop = useTwBreakpoint("lg");
  const [searchTerm, setSearchTerm] = useState("");
  const { removeFromBlacklist } = useBlacklistMutations();
  const { blacklist, isLoadingBlacklist, isBlacklistError, refetchBlacklist } =
    useBlacklist<typeof type>(type);

  if (isBlacklistError) {
    return (
      <ErrorPlaceholder
        onRetry={refetchBlacklist}
        errorMessage="Could not display blacklist"
      />
    );
  }

  if (isLoadingBlacklist) return <Loader />;

  if (!blacklist || !blacklist.length) {
    return (
      <EmptyPlaceholder
        imageSrc={EmptyStateImage}
        title={`No blacklisted ${type}`}
        subtitle={`Blacklist ${type} to exclude them from all current and future campaigns`}
        actionComponent={<AddToBlacklistButton type={type} size="md" />}
      />
    );
  }

  const filteredBlacklist = blacklist.filter((blacklistItem) => {
    const string =
      "full_name" in blacklistItem
        ? blacklistItem.full_name
        : blacklistItem.name;
    return string.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <>
      <SearchInput
        placeholder={`Search ${type}`}
        value={searchTerm}
        className="lg:max-w-80"
        variant={isDesktop ? "sm" : "lg"}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClear={() => setSearchTerm("")}
      />
      {filteredBlacklist.length === 0 ? (
        <EmptyPlaceholder
          imageSrc={NoResultsImage}
          title={`No ${type} found`}
          subtitle={`No ${type} match your search criteria. Try adjusting your search`}
          actionComponent={
            <Button onClick={() => setSearchTerm("")}>Clear search</Button>
          }
        />
      ) : (
        <section className="grid grid-cols-1 grid-rows-1 gap-2.5 lg:grid-cols-2 lg:gap-3">
          {filteredBlacklist.map((blacklistItem) => {
            if ("full_name" in blacklistItem) {
              const { id, picture_url, full_name, urn } = blacklistItem;
              return (
                <BlacklistItem
                  key={id}
                  title={full_name}
                  subtitle={getProfileSubtitle(blacklistItem)}
                  pictureUrl={picture_url}
                  onRemove={() => removeFromBlacklist({ accountUrn: urn })}
                />
              );
            }

            const { name, urn, description, logo_url } = blacklistItem;
            return (
              <BlacklistItem
                key={urn}
                title={name}
                pictureUrl={logo_url}
                subtitle={description}
                onRemove={() => removeFromBlacklist({ companyUrn: urn })}
              />
            );
          })}
        </section>
      )}
    </>
  );
}
